import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { Money, Person, PersonMonth } from './types';
import {
	Card,
	Chip,
	Crumbs,
	DASH,
	ErrorLine,
	Loading,
	Tile,
	full,
	fyOf,
	href,
	linkCls,
	monthLabel,
	monthsBetween,
	plain,
	snCls,
	sumOrNull,
	useLoad,
} from './shared';

/* ── Person: one person across months ───────────────────────────────────────── */

export const PersonView: React.FC<{
	fy: string;
	site: string | null;
	month: string | null;
	name: string;
}> = ({ fy, site, month, name }) => {
	const { data, error, loading } = useLoad<Person>(
		`/payroll/person?name=${encodeURIComponent(name)}`
	);

	// Fill the gaps, so a missing month shows as a row and not as nothing.
	type Row = PersonMonth | { wage_month: string; missing: true };
	const rows = useMemo((): Row[] => {
		const ms = data?.months || [];
		if (!ms.length) return [];
		return monthsBetween(ms[0].wage_month, ms[ms.length - 1].wage_month).flatMap((m): Row[] => {
			const hit = ms.filter(r => r.wage_month === m);
			return hit.length ? hit : [{ wage_month: m, missing: true as const }];
		});
	}, [data]);
	const paid = data?.months || [];
	const total = sumOrNull(paid.map(r => r.net_pay));

	const ID_LABEL: [keyof Person['ids'], string][] = [
		['emp_id', 'Emp ID'],
		['bank_account', 'Bank account'],
		['pan', 'PAN'],
		['uan', 'UAN'],
		['esic_no', 'ESIC no'],
	];

	return (
		<>
			<Crumbs
				items={[
					{ label: 'Payroll', to: href({ fy }) },
					...(site ? [{ label: site, to: href({ fy, site }) }] : []),
					...(site && month ? [{ label: monthLabel(month), to: href({ fy, site, month }) }] : []),
					{ label: name },
				]}
			/>
			<h1 className='text-2xl font-semibold text-gray-900 mb-1'>{name}</h1>
			<p className='text-sm text-gray-500 mb-5'>
				Every month this name appears on a final salary sheet
			</p>
			{loading && <Loading />}
			{error && <ErrorLine msg={error} />}
			{data && (
				<>
					<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3'>
						<Tile
							label='Net pay, all months'
							value={full(total)}
							sub={`${paid.length} months paid`}
						/>
						<Tile
							label='Sites'
							value={String(new Set(paid.map(r => r.site)).size)}
							sub={Array.from(new Set(paid.map(r => r.site))).join(', ')}
						/>
						<Tile
							label='Months missing'
							value={String(rows.filter(r => 'missing' in r).length)}
							sub='between first and last month'
						/>
						<Tile label='Months on hold' value={String(paid.filter(r => r.is_hold).length)} />
					</div>
					<div className='grid grid-cols-2 md:grid-cols-5 gap-2 mb-4'>
						{ID_LABEL.map(([k, label]) => (
							<div key={k} className='border border-gray-200 rounded-lg bg-white px-3 py-2'>
								<div className='text-[11px] text-gray-500'>{label}</div>
								<div className={`text-sm ${data.ids[k] ? 'text-gray-900' : 'text-gray-300'}`}>
									{data.ids[k] || DASH}
								</div>
							</div>
						))}
					</div>
					<Card>
						<div className='px-4 py-2 text-[12px] text-gray-500 border-b border-gray-100'>
							Matched by name across final sheets. Two different people with the same name will show
							together.
						</div>
						<table className='w-full text-[13px]'>
							<thead className='text-gray-500'>
								<tr>
									<th className={snCls}>#</th>
									<th className='text-left px-4 py-2 font-medium'>Wage month</th>
									<th className='text-left px-3 py-2 font-medium'>Site</th>
									<th className='text-right px-3 py-2 font-medium'>Days</th>
									<th className='text-right px-3 py-2 font-medium'>Gross</th>
									<th className='text-right px-3 py-2 font-medium'>PF</th>
									<th className='text-right px-3 py-2 font-medium'>ESIC</th>
									<th className='text-right px-3 py-2 font-medium'>PT</th>
									<th className='text-right px-3 py-2 font-medium'>TDS</th>
									<th className='text-right px-3 py-2 font-medium'>Net pay</th>
									<th className='px-3 py-2 font-medium'>Status</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((r, i) => {
									if ('missing' in r)
										return (
											<tr key={r.wage_month} className='border-t border-gray-100 text-gray-300'>
												<td className={snCls}>{i + 1}</td>
												<td className='px-4 py-1.5'>{monthLabel(r.wage_month)}</td>
												<td className='px-3 py-1.5' colSpan={9}>
													Not in any final sheet this month
												</td>
											</tr>
										);
									const prev = rows[i - 1];
									const moved = prev && !('missing' in prev) && prev.site !== r.site;
									const cls = (v: Money) =>
										`px-3 py-1.5 text-right ${v === null ? 'text-gray-300' : ''}`;
									return (
										<tr
											key={`${r.wage_month}|${r.site}`}
											className='border-t border-gray-100 hover:bg-primary-50/50'
											title={`${r.file_name} · ${r.tab_name} · row ${r.sheet_row}`}
										>
											<td className={snCls}>{i + 1}</td>
											<td className='px-4 py-1.5 whitespace-nowrap'>
												<Link
													to={href({ fy: fyOf(r.wage_month), site: r.site, month: r.wage_month })}
													className={linkCls}
												>
													{monthLabel(r.wage_month)}
												</Link>
											</td>
											<td className='px-3 py-1.5 whitespace-nowrap'>
												<Link
													to={href({ fy: fyOf(r.wage_month), site: r.site })}
													className={linkCls}
												>
													{r.site}
												</Link>{' '}
												{moved && <Chip tone='blue'>moved site</Chip>}
											</td>
											<td className={cls(r.days)}>{plain(r.days)}</td>
											<td className={cls(r.gross)}>{full(r.gross)}</td>
											<td className={cls(r.pf_ee)}>{full(r.pf_ee)}</td>
											<td className={cls(r.esi_ee)}>{full(r.esi_ee)}</td>
											<td className={cls(r.pt)}>{full(r.pt)}</td>
											<td className={cls(r.tds)}>{full(r.tds)}</td>
											<td className='px-3 py-1.5 text-right font-semibold'>{full(r.net_pay)}</td>
											<td className='px-3 py-1.5 space-x-1 whitespace-nowrap'>
												{r.is_hold && <Chip tone='amber'>On hold</Chip>}
												<Chip tone='grey'>Bank: not checked yet</Chip>
											</td>
										</tr>
									);
								})}
							</tbody>
							<tfoot className='border-t-2 border-gray-200 font-semibold'>
								<tr>
									<td />
									<td className='px-4 py-2'>Total</td>
									<td className='px-3 py-2 text-[11px] text-gray-500 font-normal'>
										{paid.length} months paid
									</td>
									{(['days', 'gross', 'pf_ee', 'esi_ee', 'pt', 'tds', 'net_pay'] as const).map(
										k => (
											<td key={k} className='px-3 py-2 text-right'>
												{k === 'days'
													? plain(sumOrNull(paid.map(r => r[k])))
													: full(sumOrNull(paid.map(r => r[k])))}
											</td>
										)
									)}
									<td />
								</tr>
							</tfoot>
						</table>
					</Card>
				</>
			)}
		</>
	);
};
